import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { MenuService } from '../services/menuService';
import { trackCategoryView } from '../services/analyticsService';
import { MenuItemWithTranslations, Category } from '../types/menu';
import { menuCategories, categoryIcons } from '../data/menuData';
import PizzaMenu from './PizzaMenu';
import MenuCategory from './MenuCategory';

const Menu: React.FC = () => {
  const [items, setItems] = useState<MenuItemWithTranslations[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('pizzas');
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const hasItemsRef = useRef(false);

  useEffect(() => {
    hasItemsRef.current = items.length > 0;
  }, [items]);

  // Load categories from RTDB so categories created in the admin dashboard
  // (e.g. a custom "Pasticcio" entry) appear in the top navigation. Falls back
  // to the static list when the database has no categories at all.
  useEffect(() => {
    let cancelled = false;
    MenuService.getCategories()
      .then((loaded) => {
        if (cancelled) return;
        setCategories(loaded);
        if (loaded.length > 0 && !loaded.some((c) => c.id === 'pizzas')) {
          setActiveCategory(loaded[0].id);
        }
      })
      .catch((error) => console.error('Failed to load categories:', error));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadMenuItems = async () => {
      // Only show the skeleton while there is nothing on screen yet; keep
      // the previous items rendered during a background refresh.
      if (!hasItemsRef.current) setLoading(true);
      setLoadError(null);
      try {
        const loadedItems = await MenuService.getMenuItems();
        if (!cancelled) setItems(loadedItems);
      } catch (error) {
        console.error('Failed to load menu items:', error);
        if (!cancelled) {
          setItems([]);
          setLoadError('Failed to load the menu. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadMenuItems();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (activeCategory !== 'all') {
      trackCategoryView(activeCategory);
    }
  }, [activeCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (!navRef.current) return;

      const footer = document.querySelector('footer');
      if (!footer) return;

      const navRect = navRef.current.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const navBottom = navRect.bottom;

      // Only change position when nav actually touches the footer
      if (navBottom >= footerRect.top) {
        const overlap = navBottom - footerRect.top;
        navRef.current.style.transform = `translateY(-${overlap}px)`;
      } else {
        navRef.current.style.transform = 'translateY(0)';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll the first item of the active category just below the sticky
  // category bar once its items have actually rendered. Falls back to the
  // top of the menu section when the category has no rendered items yet.
  const scrollToFirstItem = useCallback((category: string) => {
    const cat = categories.find(c => c.id === category);
    // Match by ID (current convention) OR by the stored name (legacy items
    // written by old admin builds) so no dish becomes invisible either way.
    const categoryItems = items.filter(item =>
      item.category === category || (cat ? item.category === cat.name : false)
    );
    const firstItemElement = categoryItems.length > 0
      ? document.getElementById(`item-${categoryItems[0].id}`)
      : null;
    const navRect = navRef.current?.getBoundingClientRect();
    const navBottom = navRect ? navRect.top + navRect.height : 160;
    if (firstItemElement) {
      const y = firstItemElement.getBoundingClientRect().top + window.pageYOffset - navBottom - 20;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    } else if (contentRef.current) {
      const y = contentRef.current.getBoundingClientRect().top + window.pageYOffset - navBottom - 20;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, [items, categories]);

  const handleCategoryClick = (category: string) => {
    if (category === activeCategory) {
      // Re-clicking the active chip: scroll the current category into view.
      scrollToFirstItem(category);
      return;
    }
    setActiveCategory(category);
    // The new category's items are not in the DOM until React re-renders,
    // so the scroll must happen after the render (see the effect below) —
    // scrolling synchronously here would look up the stale DOM and either
    // scroll nowhere (pizzas: collapsed accordions) or to the wrong spot.
    setPendingScroll(category);
  };

  useEffect(() => {
    if (pendingScroll && pendingScroll === activeCategory) {
      scrollToFirstItem(pendingScroll);
      setPendingScroll(null);
    }
  }, [activeCategory, items, pendingScroll, scrollToFirstItem]);

  // ---------------------------------------------------------------
  // Category navigation list: RTDB first (admin-managed), static fallback.
  // ---------------------------------------------------------------
  const categoryNav: Category[] =
    categories.length > 0
      ? [...categories]
          .filter((c) => c.visible !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : (Object.keys(menuCategories) as Array<keyof typeof menuCategories>).map((key, index) => ({
          id: key as string,
          name: key as string,
          order: index,
          emoji: categoryIcons[key],
          translations: { fr: '', ar: '', en: '' }
        }));

  const categoryIcon = (cat: Category): string =>
    cat.emoji || categoryIcons[cat.id as keyof typeof categoryIcons] || '🍽️';

  const categoryLabel = (cat: Category): string => {
    const i18nKey = `menu.categories.${cat.id}`;
    const translated = t(i18nKey);
    // i18n keys give the properly-cased labels for built-in categories;
    // custom database categories fall back to their stored translations.
    if (translated && translated !== i18nKey) return translated;
    return cat.translations?.[language] || cat.name || cat.id;
  };

  const renderMenuContent = () => {
    if (loading && items.length === 0) {
      // Skeleton cards mirroring the menu grid — paints faster than a full
      // spinner and reads as "content is loading" instead of a blank screen.
      return (
        <div
          className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          role="status"
          aria-label="Loading menu"
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden animate-pulse"
            >
              <div className="w-full aspect-[16/9] bg-stone-200" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-stone-200 rounded w-3/4" />
                <div className="h-3 bg-stone-200 rounded w-full" />
                <div className="h-3 bg-stone-200 rounded w-1/2" />
                <div className="h-9 bg-stone-200 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (loadError && items.length === 0) {
      return (
        <div role="alert" className="text-center py-12">
          <p className="text-red-600 mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => setReloadKey((key) => key + 1)}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white font-medium hover:opacity-90 shadow-sm"
          >
            Try again
          </button>
        </div>
      );
    }

    const activeCat = categoryNav.find(c => c.id === activeCategory);
    const categoryItems = items.filter(item =>
      item.category === activeCategory || (activeCat ? item.category === activeCat.name : false)
    );

    if (categoryItems.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No items in this category yet.</p>
        </div>
      );
    }

    // Special handling for pizza menu to remove extra space
    if (activeCategory === 'pizzas') {
      return (
        <div className="mb-0 [&>div:last-child]:mb-0">
          <PizzaMenu items={categoryItems} />
        </div>
      );
    }

    return (
      <div className="mb-0">
        <MenuCategory items={categoryItems} />
      </div>
    );
  };

  return (
    <div ref={containerRef} className="w-full scroll-smooth bg-background min-h-0 pt-16">
      {/* Categories Section */}
      <div
        ref={navRef}
        className="bg-secondary border-b border-black/40 shadow-md w-full"
        style={{
          position: 'sticky',
          top: '64px',
          zIndex: 40
        }}
      >
        <div className="max-w-screen-2xl mx-auto h-16 flex items-center overflow-hidden">
          {/* Mobile Categories (Horizontal Scroll Pills) */}
          <div className="md:hidden w-full">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide scroll-smooth px-3">
              {categoryNav.map((cat) => (
                <motion.button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  aria-pressed={activeCategory === cat.id}
                  className={`
                    flex items-center gap-1.5 h-9 px-3.5 rounded-full
                    whitespace-nowrap flex-shrink-0 transition-all duration-200
                    ${activeCategory === cat.id
                      ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-md shadow-orange-500/30'
                      : 'bg-secondary-light text-stone-300 hover:text-white'}
                  `}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-base leading-none">
                    {categoryIcon(cat)}
                  </span>
                  <span className="text-xs font-semibold">
                    {categoryLabel(cat)}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Desktop/Tablet Categories (Horizontal Scroll) */}
          <div className="hidden md:block w-full">
            <div
              ref={scrollContainerRef}
              className="flex items-center justify-center gap-2.5 overflow-x-auto scrollbar-hide scroll-smooth px-4"
            >
              {categoryNav.map((cat) => (
                <motion.button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  aria-pressed={activeCategory === cat.id}
                  className={`
                    flex items-center gap-2.5 h-10 px-4 rounded-full
                    whitespace-nowrap flex-shrink-0 transition-all duration-200
                    ${activeCategory === cat.id
                      ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-md shadow-orange-500/30'
                      : 'bg-secondary-light text-stone-300 hover:text-white hover:bg-white/10'}
                  `}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <span className="text-lg leading-none">
                    {categoryIcon(cat)}
                  </span>
                  <span className="text-sm font-semibold">
                    {categoryLabel(cat)}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Increased spacing after navigation */}
      <div className="h-6" />

      {/* Menu Items Section */}
      <div
        ref={contentRef}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1"
      >
        <div
          id="menu-items-section"
          className="scroll-mt-32 [&>div:last-child]:mb-0 [&>div:last-child]:pb-0"
        >
          {renderMenuContent()}
        </div>
      </div>

      {/* Add spacing before footer */}
      <div className="h-20" />

      {/* Remove any bottom margin/padding */}
      <style>{`
        #menu-items-section > div:last-child {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Menu;